import { Component } from 'react'

/**
 * THE APP HAD NO ERROR BOUNDARY. ANYWHERE.
 *
 * React's default when a render throws is to unmount the entire tree. With no
 * boundary that means the whole page goes blank — which is why "the customer
 * journey is down" and "it's a gray screen" were the only descriptions
 * available. A blank page names no component, no message and no line, so every
 * such failure costs a debugging session to find something the browser already
 * knew.
 *
 * This does not fix bugs. It makes them legible: the rest of the app keeps
 * working, and the part that broke says what broke.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Keep the real stack in the console for whoever opens devtools next.
    console.error('[boundary]', this.props.label || 'unnamed', error, info?.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    const { label = 'This section', onReset } = this.props
    return (
      <div
        role="alert"
        style={{
          margin: '16px 0',
          padding: '16px 18px',
          border: '1px solid var(--danger)',
          background: 'var(--danger-tint)',
          borderRadius: 'var(--radius)',
          fontSize: 'var(--fs-sm)',
          lineHeight: 1.6,
          color: 'var(--ink)',
        }}
      >
        <strong>{label} stopped working.</strong> The rest of the page is fine — only this part
        failed, and it failed rather than showing you something wrong.
        <div style={{ marginTop: 8, fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--danger)' }}>
          {String(this.state.error?.message || this.state.error)}
        </div>
        <button
          type="button"
          onClick={() => { this.setState({ error: null }); onReset?.() }}
          style={{
            marginTop: 12, padding: '7px 14px', borderRadius: 999,
            border: '1px solid var(--line)', background: 'var(--panel)',
            font: 'inherit', fontSize: 'var(--fs-sm)', cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    )
  }
}
